import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { type CSSProperties, useState } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import { useTimeoutFn } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { SlotMachineAnimatedNumber } from '@sorare/core/src/atoms/animations/SlotMachineAnimatedNumber';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { BrandXL, HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_MISSIONS_CATEGORY_RARITY,
  ANY_SPORT_SERIES_MISSIONS_COLLECT,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { MissionToastBackground } from '#src/components/missions/CurrentUserTaskUpdatedToast/MissionToast/MissionToastBackground/index.tsx';

import type { CollectionToastContent_taskInterface } from './__generated__/index.graphql.ts';

const SlideOutAnimation = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }
`;
const SlideInAnimation = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const InitialContent = styled(Vertical)`
  transform: translateX(0);
  opacity: 1;

  &.hide {
    animation: ${SlideOutAnimation} 0.6s cubic-bezier(0.4, 0.02, 0.96, 0.56)
      forwards;
  }
`;
const CompleteContent = styled(Horizontal).attrs({ center: true })`
  transform: translateX(-100%);
  opacity: 0;
  position: absolute;
  inset: 0;
  overflow: hidden;

  &.show {
    animation: ${SlideInAnimation} 0.9s cubic-bezier(0.02, 0.36, 0.48, 0.97)
      forwards;
  }
`;
const Icon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;
`;
const SecondLine = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  gap: var(--intermediate-unit);
  align-items: center;
`;
const StyledBrandXL = styled(BrandXL)`
  text-shadow: 0 0 16px var(--c-nd-500);
`;
const AnimatedLetter = styled.span`
  display: inline-block;
  transform: translateX(calc(var(--offsetFactor) * -1 * 30px));
  &.animate {
    transform: translateX(0);
  }
  transition: transform 0.9s cubic-bezier(0.02, 0.36, 0.48, 0.97);
`;
const HiddenOverflow = styled.div`
  position: relative;
  overflow: hidden;
`;

const message = defineMessage({
  id: 'CollectionToastContent.complete',
  defaultMessage: 'Complete!',
});

type Props = {
  task: CollectionToastContent_taskInterface;
  previousProgress: number;
  onClose: () => void;
};
export const CollectionToastContent = ({
  task,
  previousProgress,
  onClose,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const [displayedLiveScore, setDisplayedLiveScore] =
    useState(previousProgress);
  const [hideInitialContent, setHideInitialContent] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const { formatMessage } = useIntl();
  const {
    flags: { enableSeriesOnboardingPacks = false },
  } = useFeatureFlags();

  const isCardsCountTask = isType(task, 'CardsCountTask');
  const isCompleteCollectionsCountTask = isType(
    task,
    'CompleteCollectionsCountTask'
  );
  const isSupportedTask = isCardsCountTask || isCompleteCollectionsCountTask;

  useTimeoutFn(() => {
    if (isSupportedTask) {
      setDisplayedLiveScore(task.liveScore);
    }
  }, 200);

  if (!isSupportedTask) {
    return null;
  }

  const { title, iconUrl, liveScore, scoreToReach, color, rarity } = task;

  const isComplete = liveScore >= scoreToReach;

  const onProgressAnimationEnd = () => {
    if (isComplete) {
      setHideInitialContent(true);
    }
  };

  const completeMessage = formatMessage(message);

  return (
    <Link
      to={
        enableSeriesOnboardingPacks && rarity === Rarity.common
          ? generateSportContextPath(ANY_SPORT_SERIES_MISSIONS_COLLECT)
          : `${generateSportContextPath(ANY_SPORT_MISSIONS_CATEGORY_RARITY, {
              params: {
                rarity: Rarity.common.toLowerCase(),
                category: 'collect',
              },
            })}`
      }
      onClick={onClose}
    >
      <MissionToastBackground
        color={color}
        showHalo={hideInitialContent}
        showShimmer={hideInitialContent}
      >
        <HiddenOverflow>
          <InitialContent
            className={classNames({ hide: hideInitialContent })}
            onAnimationEnd={() => setShowComplete(true)}
          >
            <Horizontal>
              <HeadlineM>{title}</HeadlineM>
              {!!iconUrl && <Icon src={iconUrl} alt="" />}
            </Horizontal>
            <SecondLine>
              <LabelM bold>
                {isCardsCountTask ? (
                  <FormattedMessage {...fantasy.collectionMilestones} />
                ) : (
                  <FormattedMessage {...fantasy.completionMilestones} />
                )}
              </LabelM>
              <Progress
                skipStartAnimation
                value={displayedLiveScore}
                max={scoreToReach}
                color="linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)"
                onAnimationEnd={onProgressAnimationEnd}
              />
              <Horizontal gap={0}>
                <LabelM bold>
                  <SlotMachineAnimatedNumber value={displayedLiveScore} />
                </LabelM>
                <LabelM color="var(--c-nd-600)">
                  &nbsp;/&nbsp;{scoreToReach}
                </LabelM>
              </Horizontal>
            </SecondLine>
          </InitialContent>
          <CompleteContent className={classNames({ show: showComplete })}>
            <StyledBrandXL uppercase bold>
              {[...completeMessage].map((letter, index) => {
                return (
                  <AnimatedLetter
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    style={
                      {
                        '--offsetFactor': completeMessage.length - 1 - index,
                      } as CSSProperties
                    }
                    className={classNames({ animate: showComplete })}
                  >
                    {letter}
                  </AnimatedLetter>
                );
              })}
            </StyledBrandXL>
          </CompleteContent>
        </HiddenOverflow>
      </MissionToastBackground>
    </Link>
  );
};

CollectionToastContent.fragments = {
  taskInterface: gql`
    fragment CollectionToastContent_taskInterface on TaskInterface {
      id
      title
      iconUrl
      color
      ... on CardsCountTask {
        id
        liveScore
        scoreToReach
        rarity
      }
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        scoreToReach
        rarity
      }
    }
  ` as TypedDocumentNode<CollectionToastContent_taskInterface>,
};
